import React from 'react';
import {
  YesNoQuestion,
  QuestionAnswer,
  QuestionInterface
} from './questions/QuestionTypes';

// We need to create new question types for Number inputs
interface NumberQuestionProps {
  id: string;
  question: string;
  value: QuestionAnswer;
  onChange: (value: QuestionAnswer) => void;
  required?: boolean;
  error?: string;
}

const NumberQuestionComponent: React.FC<NumberQuestionProps> = ({
  id, question, value, onChange, required, error
}) => (
  <div className="mb-4">
    <label className="label">
      {question} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type="number"
      className="w-32 input"
      min="0"
      value={value.answer || ''}
      onChange={(e) => onChange({ ...value, answer: e.target.value })}
    />
    {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
  </div>
);

interface Question3Data {
  [questionId: string]: QuestionAnswer;
}

interface Step3Question3Props {
  data: Question3Data;
  onChange: (data: Question3Data) => void;
  errors: Record<string, string>;
  selectedTimeline?: string;
  questionNumber?: number;
  questionTitle?: string;
  questionDescription?: string;
  currentSection?: number;
  totalSections?: number;
}

// Timeline-based questions for Question 3 (Security & Environment)
const TIMELINE_QUESTIONS: Record<string, QuestionInterface[]> = {
  morning: [
    // 3a
    { id: '3a', question: 'Security personnel have been able to maintain order at the Polling Centre', type: 'yes_no', required: true },
    // 3b
    { id: '3b', question: 'The location of the Polling Centre was in a safe and convenient location for the women', type: 'yes_no', required: true },
    // 3c
    { id: '3c', question: 'Is there any campaigning happening around the polling centre? (within 100 metres)', type: 'yes_no', required: true },
  ],
  afternoon: [
    // 12a
    { id: '12a', question: 'Security personnel have been able to maintain order at the Polling Centre', type: 'yes_no', required: true },
    // 12b
    { id: '12b', question: 'The location of the Polling Centre was in a safe and convenient location for the women', type: 'yes_no', required: true },
    // 12c
    { id: '12c', question: 'Is there any campaigning happening around the polling centre? (within 100 metres)', type: 'yes_no', required: true },
  ],
  vote_count: [
    // 22g
    { id: '22g', question: 'You were able to capture the data of votes counted and submitted on Maso Athu', type: 'yes_no', required: true },
    // 22h
    { id: '22h', question: 'Are the school facilities (furniture, toilets, etc..) used during elections left as they were at the beginning', type: 'yes_no', required: true },
  ]
};

const QUESTION_COMPONENTS = {
  'yes_no': YesNoQuestion,
  'number': NumberQuestionComponent,
};

const Step3Question3: React.FC<Step3Question3Props> = ({ data, onChange, errors, selectedTimeline }) => {
  const updateQuestion = (questionId: string, value: QuestionAnswer) => {
    onChange({
      ...data,
      [questionId]: value
    });
  };

  // Get questions for the selected timeline with conditional filtering
  const getQuestionsForTimeline = () => {
    if (!selectedTimeline || !TIMELINE_QUESTIONS[selectedTimeline as keyof typeof TIMELINE_QUESTIONS]) {
      return [];
    }

    const allQuestions = TIMELINE_QUESTIONS[selectedTimeline as keyof typeof TIMELINE_QUESTIONS];

    // Filter out conditional questions that shouldn't be shown
    return allQuestions.filter(question => {
      if (!question.conditional) return true;

      const dependentAnswer = data?.[question.conditional.dependsOn]?.answer;
      return dependentAnswer === question.conditional.showWhen;
    });
  };

  const questions = getQuestionsForTimeline();

  const getCompletionStatus = () => {
    const completed = questions.filter(q => data?.[q.id]?.answer).length;
    return { completed, total: questions.length };
  };

  const { completed, total } = getCompletionStatus();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="mb-4 text-xl font-semibold text-gray-900">
          Question 3: Security & Location
        </h2>
        <p className="mb-4 text-gray-600">
          Timeline: {selectedTimeline ? selectedTimeline.charAt(0).toUpperCase() + selectedTimeline.slice(1).replace('_', ' ') : 'No timeline selected'}
        </p>
        {!selectedTimeline && (
          <div className="p-4 mb-4 border border-yellow-200 rounded-lg bg-yellow-50">
            <p className="text-yellow-800">Please select a timeline in Step 1 to see relevant questions for Question 3.</p>
          </div>
        )}

        {/* Progress indicator */}
        <div className="p-3 mb-6 border border-orange-200 rounded-lg bg-orange-50">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-orange-900">Question 3 Progress</span>
            <span className="text-sm text-orange-800">{completed}/{total} completed</span>
          </div>
          <div className="h-2 mt-2 bg-orange-200 rounded-full">
            <div
              className="h-2 transition-all duration-300 bg-orange-600 rounded-full"
              style={{ width: `${total > 0 ? (completed / total) * 100 : 0}%` }}
            />
          </div>
        </div>
      </div>

      {/* Questions */}
      <div className="space-y-4">
        {questions.length > 0 ? questions.map((question) => {
          const QuestionComponent = QUESTION_COMPONENTS[question.type as keyof typeof QUESTION_COMPONENTS];
          const questionValue = data?.[question.id] || {};
          const questionError = errors?.[question.id];

          return (
            <div key={question.id} className="p-4 transition-colors bg-white border border-gray-200 rounded-lg hover:border-orange-300">
              <QuestionComponent
                id={question.id}
                question={`${question.id}: ${question.question}`}
                value={questionValue}
                onChange={(value) => updateQuestion(question.id, value)}
                required={question.required}
                error={questionError}
              />
            </div>
          );
        }) : (
          <div className="py-8 text-center text-gray-500">
            {selectedTimeline ? 'No questions available for this timeline.' : 'Select a timeline to view questions.'}
          </div>
        )}
      </div>

      {/* Validation Summary */}
      {Object.keys(errors).length > 0 && (
        <div className="p-4 border border-red-200 rounded-lg bg-red-50">
          <h4 className="mb-2 font-medium text-red-900">Please complete the following required questions:</h4>
          <ul className="space-y-1 text-sm text-red-800">
            {Object.entries(errors).map(([questionId, error]) => (
              <li key={questionId}>• {questionId}: {error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Period Summary */}
      <div className="p-4 rounded-lg bg-gray-50">
        <h4 className="mb-2 font-medium text-gray-900">Summary</h4>
        <p className="text-sm text-gray-700">
          This section covers security and location aspects of the polling station including
          security personnel maintaining order and the safety/convenience of the location.
        </p>
        <div className="mt-2 text-sm text-gray-600">
          Completion: {completed}/{total} questions ({total > 0 ? Math.round((completed / total) * 100) : 0}%)
        </div>
      </div>
    </div>
  );
};

export default Step3Question3;